// ============================================
// Video Grid Component
// Displays participant video tiles in a grid
// ============================================

import React from 'react';
import VideoTile from './VideoTile';

/**
 * Calculate optimal grid layout based on participant count
 */
const getGridLayout = (participantCount) => {
  if (participantCount === 1) {
    return { cols: 1, rows: 1 };
  } else if (participantCount === 2) {
    return { cols: 2, rows: 1 };
  } else if (participantCount <= 4) {
    return { cols: 2, rows: 2 };
  } else if (participantCount <= 6) {
    return { cols: 3, rows: 2 };
  } else if (participantCount <= 9) {
    return { cols: 3, rows: 3 };
  } else if (participantCount <= 12) {
    return { cols: 4, rows: 3 };
  } else if (participantCount <= 16) {
    return { cols: 4, rows: 4 };
  } else {
    return { cols: 5, rows: Math.ceil(participantCount / 5) };
  }
};

/**
 * Video Grid - displays all participants in a responsive grid
 */
const VideoGrid = ({ peers, localPeer }) => {
  const participantCount = peers.length;
  const { cols } = getGridLayout(participantCount);

  // Grid column classes
  const gridColsClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    5: 'grid-cols-5'
  }[cols] || 'grid-cols-4';

  // For 1-2 participants, center them
  const justifyClass = participantCount <= 2 ? 'justify-center' : '';

  if (participantCount === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="text-gray-400 text-lg mb-2">Waiting for others to join...</div>
          <div className="text-gray-500 text-sm">Share the meeting link to invite participants</div>
        </div>
      </div>
    );
  }

  // Special layout for 1-on-1 calls
  if (participantCount === 1) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <div className="w-full max-w-4xl aspect-video">
          <VideoTile peer={peers[0]} isLocal={peers[0].id === localPeer?.id} isLarge />
        </div>
      </div>
    );
  }

  // Special layout for 2 participants (side by side)
  if (participantCount === 2) {
    return (
      <div className="flex items-center justify-center h-full p-4 gap-4">
        {peers.map((peer) => (
          <div key={peer.id} className="w-1/2 max-w-2xl aspect-video">
            <VideoTile peer={peer} isLocal={peer.id === localPeer?.id} isLarge />
          </div>
        ))}
      </div>
    );
  }

  // Grid layout for 3+ participants
  return (
    <div className={`grid ${gridColsClass} gap-2 p-2 h-full auto-rows-fr ${justifyClass}`}>
      {peers.map((peer) => (
        <div key={peer.id} className="relative min-h-0">
          <VideoTile peer={peer} isLocal={peer.id === localPeer?.id} />
        </div>
      ))}
    </div>
  );
};

export default VideoGrid;
